import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../global/contexts/auth/auth';
import { useEventStream } from '../hooks/useEventStream';
import { LISTENED_KINDS } from '../utils/flow';
import { STREAM_STATUS } from '../utils/notice';

// ONE CHANNEL FOR THE WHOLE PANEL, and what it keeps up to date.
//
// Until now only the live screen listened to `/events/stream`; everywhere else
// the data moved only on a reload or on coming back to the tab. This provider
// opens the channel once, for as long as the session lasts, and does two things
// with every notice:
//
//   1. REFRESHES WHAT IS ON SCREEN. Every screen that reads tasks — the board,
//      the list, the review queue, the tier progress, the plan map — reads them
//      under the `tasks` key. A notice marks those stale, and React Query asks
//      again for the ones that are MOUNTED; nothing off screen is fetched.
//   2. HANDS THE NOTICE ON to whoever subscribed (the live screen, which draws
//      it), so there is one connection and not one per screen. Core caps a
//      person at ten, and every open tab is one of them.
//
// BURSTS ARE COALESCED: authorizing a batch fires one notice per task, and ten
// refreshes in a second is ten times the same answer. One, half a second after
// the last notice.

// What is asked for, in order. Core validates `types` against the kinds that
// HAVE HAPPENED, so `review.*` is refused on a project where nobody reviewed
// anything yet — and the whole subscription with it. When the channel gives up
// on a subscription, the next one is tried; after the last, the panel polls.
const SUBSCRIPTIONS = ['task.*,review.*', 'task.*'];

// The names listened for. `EventSource` fires only for named events it has a
// listener for, so a kind missing here never arrives.
const KINDS = [...LISTENED_KINDS, 'review.decided', 'review.evaluated'];

const COALESCE_MS = 500;
const POLL_MS = 30_000;

const LiveUpdatesContext = createContext(null);

export function LiveUpdatesProvider({ children }) {
    const client = useQueryClient();
    const { isAuthenticated, can } = useAuth();
    const enabled = isAuthenticated && can('events:read');

    // Which subscription is being tried; `SUBSCRIPTIONS.length` means polling.
    const [attempt, setAttempt] = useState(0);
    const [downSeen, setDownSeen] = useState(null);
    const polling = enabled && attempt >= SUBSCRIPTIONS.length;

    const listeners = useRef(new Set());
    const timer = useRef(null);

    // The queries the live screen keeps by hand (it moves its counts from the
    // notices themselves) say so with `meta.liveManaged`, and are left alone:
    // refetching them on every notice would undo exactly what they do.
    const refreshTasks = useCallback(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            client.invalidateQueries({
                predicate: query => query.queryKey[0] === 'tasks' && !query.meta?.liveManaged,
            });
        }, COALESCE_MS);
    }, [client]);

    useEffect(() => () => clearTimeout(timer.current), []);

    const onNotice = useCallback((payload) => {
        listeners.current.forEach(listener => listener.notice?.(payload));
        refreshTasks();
    }, [refreshTasks]);

    const onResync = useCallback(() => {
        listeners.current.forEach(listener => listener.resync?.());
        refreshTasks();
    }, [refreshTasks]);

    const stream = useEventStream({
        subscribe: SUBSCRIPTIONS[attempt],
        kinds: KINDS,
        enabled: enabled && !polling,
        onNotice,
        onResync,
    });

    // A subscription the channel gave up on: try the next one. Adjusted during
    // render (not in an effect), once per attempt.
    if (enabled && stream.status === STREAM_STATUS.down && !polling && downSeen !== attempt) {
        setDownSeen(attempt);
        setAttempt(attempt + 1);
    }

    // No channel: ask again every thirty seconds, and only while somebody is
    // looking. A hidden tab has nobody to show the news to.
    useEffect(() => {
        if (!polling) return undefined;
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') refreshTasks();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [polling, refreshTasks]);

    const subscribe = useCallback((listener) => {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
    }, []);

    // Starts over from the richest subscription. Whoever calls it reloads over
    // HTTP first: a manual reopen sends no `Last-Event-ID`, so nothing replays
    // the gap.
    const { reconnect: reopen } = stream;
    const reconnect = useCallback(() => {
        setAttempt(0);
        setDownSeen(null);
        reopen();
    }, [reopen]);

    const value = useMemo(() => ({
        enabled,
        stream,
        polling,
        subscribe,
        reconnect,
        refreshTasks,
    }), [enabled, stream, polling, subscribe, reconnect, refreshTasks]);

    return <LiveUpdatesContext.Provider value={value}>{children}</LiveUpdatesContext.Provider>;
}

export function useLiveUpdates() {
    return useContext(LiveUpdatesContext);
}

// Subscribes a screen to the notices, for as long as it is mounted. The
// callbacks travel in a ref so a re-render does not resubscribe.
export function useLiveNotices({ notice, resync } = {}) {
    const live = useLiveUpdates();
    const handlers = useRef({ notice, resync });

    useEffect(() => {
        handlers.current = { notice, resync };
    }, [notice, resync]);

    const subscribe = live?.subscribe;
    useEffect(() => {
        if (!subscribe) return undefined;
        return subscribe({
            notice: payload => handlers.current.notice?.(payload),
            resync: () => handlers.current.resync?.(),
        });
    }, [subscribe]);

    return live;
}
